import React, { useEffect, useMemo, useState } from "react";
import { Checkbox } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Report } from "~/pages/Editor/EditorReports/ui/TemplatesOfReports/ManageReportForm/index";
import { SelectOption } from "~/pages/Editor/type";
import { useGetDealerFieldsLazyQuery } from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

interface ReportDealerFieldsFormProps {
  control: Control<Report>;
  setValue: (name: string, value: unknown) => void;
  error?: string;
  disabled: boolean;
}

/**
 * Поле "Поля дилера". Эндпоинт getDealerFields не поддерживает
 * searchText/page/count и возвращает весь список целиком,
 * поэтому поиск выполняется на клиенте (optionFilterProp="label"),
 * а серверная пагинация не используется.
 */
const ReportDealerFieldsForm = ({
  control,
  setValue,
  error,
  disabled,
}: ReportDealerFieldsFormProps) => {
  const { t } = useTranslation();
  const [isAllSelected, setIsAllSelected] = useState(false);

  const selectedDealerFields = useWatch({
    control,
    name: "dealerFields",
  }) as SelectOption[];

  const [getDealerFields, { data, loading }] = useGetDealerFieldsLazyQuery();

  useEffect(() => {
    getDealerFields();
  }, []);

  const rowsForSelect = useMemo<SelectOption[]>(
    () =>
      get(data, "DealerGetter.getDealerFields", []).map(
        ({ id, fieldRuDescription }) => ({
          value: id,
          label: `(${id}) ${fieldRuDescription}`,
        }),
      ),
    [data],
  );

  useEffect(() => {
    setIsAllSelected(
      rowsForSelect.length > 0 &&
        rowsForSelect.length === selectedDealerFields?.length,
    );
  }, [selectedDealerFields, rowsForSelect]);

  return (
    <>
      <Field
        type="select"
        name="dealerFields"
        control={control}
        label={t("report_list.dealerFields")}
        options={rowsForSelect}
        mode="multiple"
        labelInValue
        showSearch
        optionFilterProp="label"
        loading={loading}
        maxTagCount="responsive"
        error={error}
        disabled={disabled}
      />
      <div>
        <Checkbox
          label={t("report_list.select_all")}
          checked={isAllSelected}
          disabled={disabled}
          onChange={(e) =>
            setValue("dealerFields", e.target.checked ? rowsForSelect : [])
          }
        />
      </div>
    </>
  );
};

export default ReportDealerFieldsForm;
